import * as THREE from 'three';

/**
 * Calculate a point on a bezier curve
 * @param {number} t - The parameter value between 0 and 1
 * @param {THREE.Vector3} p0 - The first control point
 * @param {THREE.Vector3} p1 - The second control point
 * @param {THREE.Vector3} p2 - The third control point
 * @param {THREE.Vector3} p3 - The fourth control point
 * @returns {THREE.Vector3} - The point on the curve
 */
export const calculateBezierPoint = (t, p0, p1, p2, p3) => {
  try {
    // Validate inputs
    if (t < 0 || t > 1) {
      console.warn('Bezier parameter t should be between 0 and 1:', t);
      t = Math.max(0, Math.min(1, t));
    }
    
    if (!p0 || !p1 || !p2 || !p3) {
      throw new Error('Invalid bezier control points provided');
    }

    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;
    
    const result = new THREE.Vector3();
    result.multiplyVectors(p0, new THREE.Vector3(uuu, uuu, uuu));
    result.addScaledVector(p1, 3 * uu * t);
    result.addScaledVector(p2, 3 * u * tt);
    result.addScaledVector(p3, ttt);
    
    return result;
  } catch (error) {
    console.error('Error calculating bezier point:', error);
    return new THREE.Vector3(); // Return origin as fallback
  }
};

/**
 * Generate curve points from bezier handles and points
 * @param {THREE.Vector3[]} points - The points of the curve
 * @param {THREE.Vector3[]} handles - The handles of the curve
 * @param {number} resolution - The resolution of the curve
 * @returns {Float32Array} - The curve points
 */
export const generateCurvePoints = (points, handles, resolution = 100) => {
  const MIN_RESOLUTION = 10;
  const MAX_RESOLUTION = 5000;
  try {
    // Validate inputs
    if (!points || !Array.isArray(points) || points.length < 3) {
      throw new Error('Invalid points array: need at least 3 points');
    }
    
    if (!handles || !Array.isArray(handles) || handles.length !== points.length) {
      throw new Error('Handles array must match points array length');
    }

    if (resolution < MIN_RESOLUTION || resolution > MAX_RESOLUTION) {
      console.warn('Resolution should be between 10-1000:', resolution);
      resolution = Math.max(MIN_RESOLUTION, Math.min(MAX_RESOLUTION, resolution));
    }

    const curvePoints = [];
    const numPoints = points.length;
    
    for (let i = 0; i < numPoints; i++) {
      try {
        const current = new THREE.Vector3(points[i].x, points[i].y, points[i].z);
        const next = new THREE.Vector3(points[(i + 1) % numPoints].x, points[(i + 1) % numPoints].y, points[(i + 1) % numPoints].z);
        
        const currentOut = new THREE.Vector3(handles[i].out.x, handles[i].out.y, handles[i].out.z).add(current);
        const nextIn = new THREE.Vector3(handles[(i + 1) % numPoints].in.x, handles[(i + 1) % numPoints].in.y, handles[(i + 1) % numPoints].in.z).add(next);
        
        for (let t = 0; t < 1; t += 1 / resolution) {
          const point = calculateBezierPoint(t, current, currentOut, nextIn, next);
          if (point && isFinite(point.x) && isFinite(point.y) && isFinite(point.z)) {
            curvePoints.push(point.x, point.y, point.z);
          }
        }
      } catch (segmentError) {
        console.error(`Error processing curve segment ${i}:`, segmentError);
      }
    }
    
    if (curvePoints.length === 0) {
      throw new Error('No valid curve points generated');
    }
    
    return new Float32Array(curvePoints);
  } catch (error) {
    console.error('Error generating curve points:', error);
    // Return a simple fallback curve (straight line)
    const fallbackPoints = [0, 0, 0, 1, 0, 0];
    return new Float32Array(fallbackPoints);
  }
};

/**
 * Project curve points onto a mesh
 * @param {Float32Array} curvePositions - The curve positions
 * @param {THREE.Mesh} mesh - The mesh to project onto
 * @param {Object} bvh - The bounding volume hierarchy of the mesh
 * @returns {Float32Array} - The projected curve positions
 */
export const projectCurvePointsOntoMesh = (curvePositions, mesh, bvh) => {
  try {
    if (!mesh || !bvh || !curvePositions || curvePositions.length === 0) return curvePositions;

    const projected = new Float32Array(curvePositions.length);
    const { center, size } = getMeshWorldBounds(mesh);

    // Choose a push distance guaranteed to be outside the mesh in XZ
    const pushDistance = Math.max(size.x, size.z) * 1.25;

    // Prepare transforms for BVH local raycast
    const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
    const localToWorld = mesh.matrixWorld;

    // Reusable objects to avoid GC
    const tmpOrigin = new THREE.Vector3();
    const tmpDir = new THREE.Vector3();
    const tmpLocalRay = new THREE.Ray();
    const tmpWorldPoint = new THREE.Vector3();

    for (let i = 0; i < curvePositions.length; i += 3) {
      const px = curvePositions[i];
      const py = curvePositions[i + 1];
      const pz = curvePositions[i + 2];

      // Outward direction on XZ plane from mesh center
      tmpDir.set(px - center.x, 0, pz - center.z);
      const len = tmpDir.length();
      if (len < 1e-5) {
        // Degenerate: pick arbitrary outward direction
        tmpDir.set(1, 0, 0);
      } else {
        tmpDir.multiplyScalar(1 / len);
      }

      // Start from outside point and cast back inward
      tmpOrigin.set(px + tmpDir.x * pushDistance, py, pz + tmpDir.z * pushDistance);
      const inwardDir = tmpDir.clone().multiplyScalar(-1);

      // Build local-space ray
      tmpLocalRay.origin.copy(tmpOrigin).applyMatrix4(worldToLocal);
      tmpLocalRay.direction.copy(inwardDir).transformDirection(worldToLocal).normalize();

      const hit = bvh.raycastFirst(tmpLocalRay);
      if (hit && hit.point) {
        tmpWorldPoint.copy(hit.point).applyMatrix4(localToWorld);
        projected[i] = tmpWorldPoint.x;
        projected[i + 1] = tmpWorldPoint.y;
        projected[i + 2] = tmpWorldPoint.z;
        continue;
      }

      // Fallback: cast downward from above bbox top
      tmpOrigin.set(px, center.y + size.y * 1.5, pz);
      tmpLocalRay.origin.copy(tmpOrigin).applyMatrix4(worldToLocal);
      tmpLocalRay.direction.set(0, -1, 0).transformDirection(worldToLocal).normalize();
      const downHit = bvh.raycastFirst(tmpLocalRay);
      if (downHit && downHit.point) {
        tmpWorldPoint.copy(downHit.point).applyMatrix4(localToWorld);
        projected[i] = tmpWorldPoint.x;
        projected[i + 1] = tmpWorldPoint.y;
        projected[i + 2] = tmpWorldPoint.z;
        continue;
      }

      // As last resort, keep original point
      projected[i] = px;
      projected[i + 1] = py;
      projected[i + 2] = pz;
    }

    return projected;
  } catch (error) {
    console.error('Error projecting curve points onto mesh:', error);
    return curvePositions;
  }
};

/**
 * Project each curve sample to the closest point on the mesh (BVH closestPointToPoint).
 * Use this for closed curves on folded / non-convex scans where {@link projectCurvePointsOntoMesh}
 * (horizontal ray + downward fallback) jumps between surface sheets and looks jagged.
 *
 * @param {Float32Array} curvePositions
 * @param {THREE.Mesh} mesh
 * @param {Object} bvh - Mesh BVH (geometry.boundsTree)
 * @returns {Float32Array}
 */
export const projectCurvePointsOntoMeshClosest = (curvePositions, mesh, bvh) => {
  try {
    if (!mesh || !bvh || !curvePositions || curvePositions.length === 0) return curvePositions;

    const projected = new Float32Array(curvePositions.length);
    const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
    const localToWorld = mesh.matrixWorld;
    const { center } = getMeshWorldBounds(mesh);

    const tmpWorld = new THREE.Vector3();
    const localPoint = new THREE.Vector3();
    const worldOut = new THREE.Vector3();
    const target = { point: new THREE.Vector3() };

    for (let i = 0; i < curvePositions.length; i += 3) {
      tmpWorld.set(curvePositions[i], curvePositions[i + 1], curvePositions[i + 2]);
      localPoint.copy(tmpWorld).applyMatrix4(worldToLocal);

      const hit = bvh.closestPointToPoint(localPoint, target);
      if (hit && target.point) {
        worldOut.copy(target.point).applyMatrix4(localToWorld);
        projected[i] = worldOut.x;
        projected[i + 1] = worldOut.y;
        projected[i + 2] = worldOut.z;
        continue;
      }

      const fb = projectPointOntoMesh(
        { x: tmpWorld.x, y: tmpWorld.y, z: tmpWorld.z },
        center,
        bvh,
        mesh
      );
      if (fb && typeof fb.x === 'number') {
        projected[i] = fb.x;
        projected[i + 1] = fb.y;
        projected[i + 2] = fb.z;
      } else {
        projected[i] = curvePositions[i];
        projected[i + 1] = curvePositions[i + 1];
        projected[i + 2] = curvePositions[i + 2];
      }
    }

    return projected;
  } catch (error) {
    console.error('Error in projectCurvePointsOntoMeshClosest:', error);
    return curvePositions;
  }
};

/**
 * Project a point onto a mesh
 * @param {THREE.Vector3} point - The point to project
 * @param {THREE.Vector3} targetCenter - The center of the mesh
 * @param {Object} bvh - The bounding volume hierarchy of the mesh
 * @returns {THREE.Vector3} - The projected point
 */
export const projectPointOntoMesh = (point, targetCenter, bvh, mesh) => {
  if (!mesh || !bvh) return point;

  // BVH operates in local (geometry) space, so we need to transform rays accordingly
  const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const localToWorld = mesh.matrixWorld;

  const origin = new THREE.Vector3(point.x, point.y, point.z);

  // The most reliable direction is from the point towards the center of the mesh
  // Project only in the XZ plane (ignore Y values)
  const flatOrigin = origin.clone(); flatOrigin.y = 0;
  const flatTarget = targetCenter.clone(); flatTarget.y = 0;
  const direction = new THREE.Vector3().subVectors(flatTarget, flatOrigin).normalize();

  // Transform ray to local space
  const localRay = new THREE.Ray();
  localRay.origin.copy(origin).applyMatrix4(worldToLocal);
  localRay.direction.copy(direction).transformDirection(worldToLocal).normalize();

  // Use BVH-accelerated raycasting in local space
  const intersects = bvh.raycastFirst(localRay);

  if (intersects) {
    // Transform hit point back to world space
    const worldPoint = new THREE.Vector3().copy(intersects.point).applyMatrix4(localToWorld);
    return { x: worldPoint.x, y: worldPoint.y, z: worldPoint.z };
  }

  // Fallback if the primary raycast fails (should be rare)
  console.warn('Could not project point towards center, using downward projection as fallback.');
  localRay.origin.copy(origin).applyMatrix4(worldToLocal);
  localRay.direction.set(0, -1, 0).transformDirection(worldToLocal).normalize();

  const fallbackIntersects = bvh.raycastFirst(localRay);
  if (fallbackIntersects) {
      const worldPoint = new THREE.Vector3().copy(fallbackIntersects.point).applyMatrix4(localToWorld);
      return { x: worldPoint.x, y: worldPoint.y, z: worldPoint.z };
  }

  const fromAbove = projectXZOntoSocketSurface(point.x, point.z, mesh, bvh, targetCenter);
  if (fromAbove) return fromAbove;

  const localPoint = origin.clone().applyMatrix4(worldToLocal);
  const closestTarget = { point: new THREE.Vector3() };
  if (bvh.closestPointToPoint(localPoint, closestTarget) && closestTarget.point) {
    const worldPoint = closestTarget.point.clone().applyMatrix4(localToWorld);
    return { x: worldPoint.x, y: worldPoint.y, z: worldPoint.z };
  }

  console.error('Could not project point onto surface at all, using original position:', point);
  return point;
};

/**
 * Project a point onto the top surface of a mesh by casting downward from above.
 * Used for "high" saddle points to ensure they land on the mesh rim (highest Y at given XZ).
 * @param {number} x - World X
 * @param {number} z - World Z
 * @param {THREE.Box3} bbox - Mesh world bounding box
 * @param {THREE.Vector3} size - Mesh world size (bbox.getSize)
 * @param {Object} bvh - BVH for the mesh
 * @param {THREE.Mesh} mesh - The mesh
 * @returns {{x,y,z}|null} - Projected point or null if no hit
 */
export const projectPointOntoMeshFromAbove = (x, z, bbox, size, bvh, mesh) => {
  if (!mesh || !bvh) return null;

  const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const localToWorld = mesh.matrixWorld;

  const originY = bbox.max.y + Math.max(size.x, size.y, size.z) * 0.5;

  const localRay = new THREE.Ray();
  const originWorld = new THREE.Vector3(x, originY, z);
  localRay.origin.copy(originWorld).applyMatrix4(worldToLocal);
  localRay.direction.set(0, -1, 0).transformDirection(worldToLocal).normalize();

  const hit = bvh.raycastFirst(localRay);
  if (hit?.point) {
    const p = new THREE.Vector3().copy(hit.point).applyMatrix4(localToWorld);
    return { x: p.x, y: p.y, z: p.z };
  }
  return null;
};

/**
 * Project a world XZ location onto the socket exterior at that location's local height.
 * Uses downward raycasting per XZ (not a global Y). Walks inward toward the socket center
 * when the rim XZ lies outside the mesh footprint.
 *
 * @param {number} x
 * @param {number} z
 * @param {THREE.Mesh} mesh
 * @param {Object} bvh
 * @param {THREE.Vector3} [inwardTarget] - Target to walk toward when rim XZ misses (default: mesh center)
 * @returns {{x:number,y:number,z:number}|null}
 */
export const projectXZOntoSocketSurface = (x, z, mesh, bvh, inwardTarget = null) => {
  if (!mesh || !bvh) return null;

  if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
  const bbox = mesh.geometry.boundingBox.clone();
  bbox.applyMatrix4(mesh.matrixWorld);
  const size = bbox.getSize(new THREE.Vector3());
  const center = bbox.getCenter(new THREE.Vector3());
  const target = inwardTarget ?? center;

  const tryAt = (tx, tz) => projectPointOntoMeshFromAbove(tx, tz, bbox, size, bvh, mesh);

  let hit = tryAt(x, z);
  if (hit) return hit;

  const steps = 16;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    hit = tryAt(x + (target.x - x) * t, z + (target.z - z) * t);
    if (hit) return hit;
  }

  const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const localToWorld = mesh.matrixWorld;
  const probeWorld = new THREE.Vector3(x, bbox.max.y + size.y, z);
  const localPoint = probeWorld.clone().applyMatrix4(worldToLocal);
  const closestTarget = { point: new THREE.Vector3() };
  if (bvh.closestPointToPoint(localPoint, closestTarget) && closestTarget.point) {
    const p = closestTarget.point.clone().applyMatrix4(localToWorld);
    return { x: p.x, y: p.y, z: p.z };
  }

  return null;
};

/**
 * Snap an existing world point onto the nearest socket surface.
 * @param {{x:number,y:number,z:number}} point
 * @param {THREE.Mesh} mesh
 * @param {Object} bvh
 * @returns {{x:number,y:number,z:number}}
 */
export const snapPointOntoSocketSurface = (point, mesh, bvh) => {
  if (!mesh || !bvh || !point) return point;

  const onSurface = projectXZOntoSocketSurface(point.x, point.z, mesh, bvh);
  if (onSurface) return onSurface;

  const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const localToWorld = mesh.matrixWorld;
  const localPoint = new THREE.Vector3(point.x, point.y, point.z).applyMatrix4(worldToLocal);
  const closestTarget = { point: new THREE.Vector3() };
  if (bvh.closestPointToPoint(localPoint, closestTarget) && closestTarget.point) {
    const p = closestTarget.point.clone().applyMatrix4(localToWorld);
    return { x: p.x, y: p.y, z: p.z };
  }

  return point;
};

/**
 * Place a saddle seed on the socket surface using a target Y (partial rim / low back).
 * Does not snap to full local rim height — preserves the classic saddle profile while
 * staying on geometry via inward ray, downward ray, and closest-point fallbacks.
 */
export const projectSeedOntoSurfaceAtTarget = (x, z, targetY, mesh, bvh, inwardTarget = null) => {
  if (!mesh || !bvh) return { x, y: targetY, z };

  const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const localToWorld = mesh.matrixWorld;
  const { center } = getMeshWorldBounds(mesh);
  const targetCenter = inwardTarget ?? center;

  const origin = new THREE.Vector3(x, targetY, z);
  const flatOrigin = origin.clone();
  flatOrigin.y = 0;
  const flatTarget = targetCenter.clone();
  flatTarget.y = 0;
  const direction = new THREE.Vector3().subVectors(flatTarget, flatOrigin);
  if (direction.lengthSq() < 1e-8) direction.set(0, 0, -1);
  else direction.normalize();

  const localRay = new THREE.Ray();
  localRay.origin.copy(origin).applyMatrix4(worldToLocal);
  localRay.direction.copy(direction).transformDirection(worldToLocal).normalize();

  const inwardHit = bvh.raycastFirst(localRay);
  if (inwardHit?.point) {
    const p = inwardHit.point.clone().applyMatrix4(localToWorld);
    return { x: p.x, y: p.y, z: p.z };
  }

  localRay.origin.copy(origin).applyMatrix4(worldToLocal);
  localRay.direction.set(0, -1, 0).transformDirection(worldToLocal).normalize();
  const downHit = bvh.raycastFirst(localRay);
  if (downHit?.point) {
    const p = downHit.point.clone().applyMatrix4(localToWorld);
    return { x: p.x, y: p.y, z: p.z };
  }

  const localPoint = origin.clone().applyMatrix4(worldToLocal);
  const closestTarget = { point: new THREE.Vector3() };
  if (bvh.closestPointToPoint(localPoint, closestTarget) && closestTarget.point) {
    const p = closestTarget.point.clone().applyMatrix4(localToWorld);
    return { x: p.x, y: p.y, z: p.z };
  }

  const steps = 12;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const tx = x + (targetCenter.x - x) * t;
    const tz = z + (targetCenter.z - z) * t;
    localRay.origin.set(tx, targetY, tz).applyMatrix4(worldToLocal);
    localRay.direction.set(0, -1, 0).transformDirection(worldToLocal).normalize();
    const walkHit = bvh.raycastFirst(localRay);
    if (walkHit?.point) {
      const p = walkHit.point.clone().applyMatrix4(localToWorld);
      return { x: p.x, y: p.y, z: p.z };
    }
  }

  return { x, y: targetY, z };
};

export const TRIMLINE_DEFAULT_POINT_COUNT = 8;

/** Anchor count for the landmark-driven (Mid Patellar + condyles) trimline loop. */
export const TRIMLINE_LANDMARK_POINT_COUNT = 8;

/**
 * Per-anchor symmetric bezier handle lengths (world units / cm) for the 8-anchor landmark loop.
 * Short front-bottom pair → narrow tight radius + steep walls; longer back-bottom pair → broad U;
 * the paired condyle-height points medium. Indexed to match {@link buildTrimlineLandmarkSeeds}
 * output order.
 */
const TRIMLINE_LANDMARK_HANDLE_LENGTHS = [1.5, 2.0, 2.0, 3.0, 3.0, 2.0, 2.0, 1.5];

/**
 * Closed 8-point trimline saddle — angles from front (Patela), CCW viewed from above.
 * Height pattern: 4 rim-high corners (0,3,4,7) → shallow front pair (1,2) → deep back pair (5,6).
 */
export const TRIMLINE_8POINT_SEED_SPEC = [
  { angleFromFront: (5 * Math.PI) / 8, role: 'high' },    // 0 left-front rim
  { angleFromFront: (3 * Math.PI) / 8, role: 'shallow' }, // 1 front dip
  { angleFromFront: (1 * Math.PI) / 8, role: 'shallow' }, // 2 front dip
  { angleFromFront: (-1 * Math.PI) / 8, role: 'high' },   // 3 right-front rim
  { angleFromFront: (-3 * Math.PI) / 8, role: 'high' },   // 4 right-back rim
  { angleFromFront: (-5 * Math.PI) / 8, role: 'back' },   // 5 back low
  { angleFromFront: (-7 * Math.PI) / 8, role: 'back' },   // 6 back low
  { angleFromFront: (7 * Math.PI) / 8, role: 'high' },    // 7 left-back rim
];

/** Saddle height targets relative to a valley (Patela) Y — partial rim, not full socket top. */
export const computeSaddleSeedTargetY = (role, { valleyY, bboxMaxY, meshSizeY, centerY }) => {
  const rimY = (valleyY + bboxMaxY) * 0.5;
  const backY = valleyY - meshSizeY * 0.08;
  const shallowY = valleyY - meshSizeY * 0.015;
  const midY = valleyY + meshSizeY * 0.04;
  switch (role) {
    case 'front':
    case 'valley':
      return valleyY;
    case 'back':
    case 'low':
      return backY;
    case 'shallow':
      return shallowY;
    case 'mid':
      return midY;
    case 'rim':
    case 'high':
      return rimY;
    default:
      return centerY ?? valleyY;
  }
};

/**
 * Pure XZ translation so patela click lands on front arc between shallow seeds
 * (points 1 & 2). Ellipse angles, radius, and height roles stay unchanged.
 */
const computeTrimlinePlacementOffsetForPatela = (meshCenter, patelaPoint, radius, dirXZ) => {
  const rightXZ = new THREE.Vector3(-dirXZ.z, 0, dirXZ.x);
  const angle1 = TRIMLINE_8POINT_SEED_SPEC[1].angleFromFront;
  const angle2 = TRIMLINE_8POINT_SEED_SPEC[2].angleFromFront;
  const midAngle = (angle1 + angle2) * 0.5;
  const cos = Math.cos(midAngle);
  const sin = Math.sin(midAngle);
  const anchorX = meshCenter.x + dirXZ.x * cos * radius + rightXZ.x * sin * radius;
  const anchorZ = meshCenter.z + dirXZ.z * cos * radius + rightXZ.z * sin * radius;
  return {
    x: patelaPoint.x - anchorX,
    z: patelaPoint.z - anchorZ,
  };
};

/**
 * Build default 8 anchor points on the socket surface (saddle profile).
 */
export const buildTrimline8PointSeeds = ({
  mesh,
  bvh,
  patelaPoint,
  center,
  size,
  bbox,
}) => {
  if (!mesh || !bvh) return [];

  const valleyY = patelaPoint?.y ?? center.y;
  const saddleCtx = {
    valleyY,
    bboxMaxY: bbox.max.y,
    meshSizeY: size.y,
    centerY: center.y,
  };

  const radius = Math.max(
    patelaPoint
      ? Math.hypot(patelaPoint.x - center.x, patelaPoint.z - center.z)
      : 0,
    Math.max(size.x, size.z) * 0.5
  );

  let dirXZ;
  if (patelaPoint) {
    dirXZ = new THREE.Vector3(patelaPoint.x - center.x, 0, patelaPoint.z - center.z);
    if (dirXZ.lengthSq() < 1e-8) dirXZ.set(0, 0, 1);
    else dirXZ.normalize();
  } else {
    dirXZ = new THREE.Vector3(0, 0, 1);
  }

  const placementOffset = patelaPoint
    ? computeTrimlinePlacementOffsetForPatela(center, patelaPoint, radius, dirXZ)
    : { x: 0, z: 0 };
  const seedCenter = new THREE.Vector3(
    center.x + placementOffset.x,
    center.y,
    center.z + placementOffset.z
  );

  const rightXZ = new THREE.Vector3(-dirXZ.z, 0, dirXZ.x);
  const rx = radius;
  const rz = radius;

  return TRIMLINE_8POINT_SEED_SPEC.map(({ angleFromFront, role }) => {
    const cos = Math.cos(angleFromFront);
    const sin = Math.sin(angleFromFront);
    const x = seedCenter.x + dirXZ.x * cos * rz + rightXZ.x * sin * rx;
    const z = seedCenter.z + dirXZ.z * cos * rz + rightXZ.z * sin * rx;
    const targetY = computeSaddleSeedTargetY(role, saddleCtx);
    return projectSeedOntoSurfaceAtTarget(x, z, targetY, mesh, bvh, seedCenter);
  });
};

/**
 * Build an 8-anchor closed trimline loop directly from Axes & Alignment landmarks.
 *
 * Loop order (CCW around the socket, symmetric about the front↔back axis):
 *   0 front-bottom (EC side)   — narrow 3 cm front valley bottom, 2 cm below Mid Patellar
 *   1 EC high, toward front    — condyle-height point flanking the front dip
 *   2 EC high, toward back     — condyle-height point flanking the back dip
 *   3 back-bottom (EC side)    — broad 6 cm popliteal valley bottom
 *   4 back-bottom (IC side)
 *   5 IC high, toward back     — condyle-height point flanking the back dip
 *   6 IC high, toward front    — condyle-height point flanking the front dip
 *   7 front-bottom (IC side)
 * Anchors 7↔0 straddle the Mid Patellar Point (lowest point centred on it); 3↔4 give the
 * 6 cm flat back bottom. Each condyle keeps a PAIR of high points (one toward the front dip,
 * one toward the back dip) instead of a single peak, so the rim sits flat over the condyle and
 * the user gets a control point on either side of both descents. There are no separate wall
 * shoulders — the curve rises straight from the front/back valley bottoms to the condyle pair.
 * Shape (narrow steep front vs broad U back) comes from anchor Y plus the per-anchor handle
 * lengths returned alongside the points, which keep the curve smooth (tangent-continuous).
 *
 * @param {Object} params
 * @param {THREE.Mesh} params.mesh
 * @param {Object} params.bvh
 * @param {{x,y,z}} params.midPatellar - world-space Mid Patellar landmark
 * @param {{x,y,z}} params.internalCondyle - world-space Internal Condyle landmark
 * @param {{x,y,z}} params.externalCondyle - world-space External Condyle landmark
 * @param {THREE.Vector3} params.center - socket reference centre (condyle-midpoint XZ)
 * @param {THREE.Vector3} params.size - mesh world size
 * @returns {{ points: Array<{x,y,z}>, handleLengths: number[] }}
 */
export const buildTrimlineLandmarkSeeds = ({
  mesh,
  bvh,
  midPatellar,
  internalCondyle,
  externalCondyle,
  center,
  size,
}) => {
  if (!mesh || !bvh || !midPatellar || !internalCondyle || !externalCondyle) {
    return { points: [], handleLengths: [] };
  }

  const FRONT_CLEARANCE_CM = 2;   // valley bottom sits 2 cm below the Mid Patellar Point
  const FRONT_HALF_WIDTH_CM = 1.5; // narrow 3 cm front bottom = ±1.5 cm around mid patellar
  const BACK_HALF_WIDTH_CM = 3;    // broad 6 cm back bottom = ±3 cm around the back centreline
  const BACK_EXTRA_DROP_CM = 2;    // lower the back (popliteal) valley bottom by this much more
  const HIGH_TOWARD_BOTTOM = 0.5;  // 0 = high points at condyle width, 1 = directly above the dip
  const BACK4_TOWARD_IC = 0.3;     // pull back-bottom IC (point 4) toward the IC condyle / point 5

  // Horizontal frame centred on the socket reference.
  const frontDir = new THREE.Vector3(midPatellar.x - center.x, 0, midPatellar.z - center.z);
  if (frontDir.lengthSq() < 1e-8) frontDir.set(0, 0, 1);
  else frontDir.normalize();

  // XZ-perpendicular "side" axis (frontDir rotated 90° about Y), oriented toward the
  // external condyle so the +sideAxis anchors land on the EC side.
  const sideAxis = new THREE.Vector3(-frontDir.z, 0, frontDir.x);
  const ecSide = new THREE.Vector3(externalCondyle.x - center.x, 0, externalCondyle.z - center.z);
  if (sideAxis.dot(ecSide) < 0) sideAxis.negate();

  const radius = Math.max(size.x, size.z) * 0.5;
  const backCenter = new THREE.Vector3(
    center.x - frontDir.x * radius,
    center.y,
    center.z - frontDir.z * radius
  );

  const frontBottomY = midPatellar.y - FRONT_CLEARANCE_CM;
  // Back valley bottom depth = current opposite-side dip depth (valleyY - sizeY * 0.08),
  // dropped a further BACK_EXTRA_DROP_CM so the popliteal dip sits lower.
  const backY =
    computeSaddleSeedTargetY('back', {
      valleyY: midPatellar.y,
      bboxMaxY: center.y + size.y * 0.5,
      meshSizeY: size.y,
      centerY: center.y,
    }) - BACK_EXTRA_DROP_CM;

  const place = (x, z, targetY) =>
    projectSeedOntoSurfaceAtTarget(x, z, targetY, mesh, bvh, center);

  // XZ-only linear blend (height comes from the place() target Y).
  const lerpXZ = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });

  // Front-bottom pair straddling the Mid Patellar Point (narrow rounded bottom).
  const frontEC = { x: midPatellar.x + sideAxis.x * FRONT_HALF_WIDTH_CM, z: midPatellar.z + sideAxis.z * FRONT_HALF_WIDTH_CM };
  const frontIC = { x: midPatellar.x - sideAxis.x * FRONT_HALF_WIDTH_CM, z: midPatellar.z - sideAxis.z * FRONT_HALF_WIDTH_CM };

  // Back-bottom pair (broad popliteal valley, BACK_HALF_WIDTH_CM either side).
  // Point 4 (IC side) is pulled horizontally toward the IC condyle / point 5.
  const backEC = { x: backCenter.x + sideAxis.x * BACK_HALF_WIDTH_CM, z: backCenter.z + sideAxis.z * BACK_HALF_WIDTH_CM };
  const backICraw = { x: backCenter.x - sideAxis.x * BACK_HALF_WIDTH_CM, z: backCenter.z - sideAxis.z * BACK_HALF_WIDTH_CM };
  const backIC = lerpXZ(backICraw, internalCondyle, BACK4_TOWARD_IC);

  // Two high points per side at condyle height, pulled horizontally toward the
  // valley-bottom point each one flanks (HIGH_TOWARD_BOTTOM). This keeps the
  // front highs (1,6) sitting above the front-bottom pair (0,7) and the back
  // highs (2,5) above the back-bottom pair (3,4), instead of out at full
  // condyle width — giving a near-vertical rise from each dip to its plateau.
  const ecHighFront = lerpXZ(externalCondyle, frontEC, HIGH_TOWARD_BOTTOM);  // 1
  const ecHighBack = lerpXZ(externalCondyle, backEC, HIGH_TOWARD_BOTTOM);    // 2
  const icHighBack = lerpXZ(internalCondyle, backIC, HIGH_TOWARD_BOTTOM);    // 5
  const icHighFront = lerpXZ(internalCondyle, frontIC, HIGH_TOWARD_BOTTOM);  // 6

  const points = [
    place(frontEC.x, frontEC.z, frontBottomY),                       // 0 front bottom, EC side
    place(ecHighFront.x, ecHighFront.z, externalCondyle.y),          // 1 EC high, toward front dip
    place(ecHighBack.x, ecHighBack.z, externalCondyle.y),            // 2 EC high, toward back dip
    place(backEC.x, backEC.z, backY),                                // 3 back bottom, EC side
    place(backIC.x, backIC.z, backY),                                // 4 back bottom, IC side
    place(icHighBack.x, icHighBack.z, internalCondyle.y),            // 5 IC high, toward back dip
    place(icHighFront.x, icHighFront.z, internalCondyle.y),          // 6 IC high, toward front dip
    place(frontIC.x, frontIC.z, frontBottomY),                       // 7 front bottom, IC side
  ];

  // All anchors use the same smooth Catmull-Rom tangent equation (no per-anchor
  // direction overrides). The broad popliteal U comes from the back-bottom
  // anchor positions (lowered valley, 6 cm spread) plus their longer handle
  // lengths — not from forcing the handle direction, which previously produced a
  // kink once the loop winding was canonicalized (reversed).
  const handleDirs = new Array(points.length).fill(null);

  let handleLengths = [...TRIMLINE_LANDMARK_HANDLE_LENGTHS];
  let outPoints = points;
  let outHandleDirs = handleDirs;

  // ── Canonicalize the loop winding ────────────────────────────────────────
  // The downstream cut classifies vertices with the per-sample cut-plane normal
  // (tangent × surface normal). That normal's sign — and therefore which side is
  // trimmed — flips when the loop's traversal winding flips, which happens when
  // the external/internal condyles land on opposite sides. To keep the cut
  // deterministic (always trim the upper rim, keep the lower body) WITHOUT
  // altering the curve, reverse the loop's traversal order when its net cut
  // normal points up. Reversal yields the identical curve (Catmull-Rom tangents
  // are recomputed from order; the U-shape handle overrides are symmetric), it
  // only flips the winding so the cut normals end up pointing toward the body.
  let cutNormalSumY = 0;
  const tan = new THREE.Vector3();
  const crs = new THREE.Vector3();
  for (let i = 0; i < points.length; i++) {
    const prev = points[(i - 1 + points.length) % points.length];
    const next = points[(i + 1) % points.length];
    tan.set(next.x - prev.x, next.y - prev.y, next.z - prev.z);
    if (tan.lengthSq() < 1e-10) continue;
    tan.normalize();
    const sn = getSurfaceNormalAtPoint(points[i], mesh, bvh);
    crs.crossVectors(tan, sn);
    cutNormalSumY += crs.y;
  }

  if (cutNormalSumY > 0) {
    outPoints = points.slice().reverse();
    handleLengths = handleLengths.reverse();
    outHandleDirs = handleDirs.slice().reverse();
  }

  return { points: outPoints, handleLengths, handleDirs: outHandleDirs };
};

/**
 * Build a structured log payload describing the closed trimline Bezier curve.
 * @param {Array<{x,y,z}>} points
 * @param {Array<{in:{x,y,z},out:{x,y,z}}>} bezierHandles
 * @param {{x,y,z}|null} [patelaPoint]
 */
export const buildTrimlineCurveEquationLog = (points, bezierHandles, patelaPoint = null) => {
  if (!points?.length || !bezierHandles?.length || points.length !== bezierHandles.length) {
    return null;
  }

  const n = points.length;
  const valleyY = patelaPoint?.y ?? points[0]?.y;
  const segments = [];

  for (let i = 0; i < n; i++) {
    const next = (i + 1) % n;
    const p0 = points[i];
    const p3 = points[next];
    const p1 = {
      x: p0.x + bezierHandles[i].out.x,
      y: p0.y + bezierHandles[i].out.y,
      z: p0.z + bezierHandles[i].out.z,
    };
    const p2 = {
      x: p3.x + bezierHandles[next].in.x,
      y: p3.y + bezierHandles[next].in.y,
      z: p3.z + bezierHandles[next].in.z,
    };

    segments.push({
      segmentIndex: i,
      from: i,
      to: next,
      equation: 'B(t) = (1-t)^3*P0 + 3(1-t)^2*t*P1 + 3(1-t)*t^2*P2 + t^3*P3, t in [0,1]',
      P0: { ...p0 },
      P1: { ...p1 },
      P2: { ...p2 },
      P3: { ...p3 },
      handleIn: { ...bezierHandles[next].in },
      handleOut: { ...bezierHandles[i].out },
      deltaY_fromValley: {
        P0: p0.y - valleyY,
        P1: p1.y - valleyY,
        P2: p2.y - valleyY,
        P3: p3.y - valleyY,
      },
    });
  }

  return {
    closed: true,
    pointCount: n,
    valleyY,
    anchorPoints: points.map((p, i) => ({
      index: i,
      x: p.x,
      y: p.y,
      z: p.z,
      deltaY_fromValley: p.y - valleyY,
      seedRole: TRIMLINE_8POINT_SEED_SPEC[i]?.role ?? null,
    })),
    bezierHandles,
    segments,
  };
};

/**
 * Get the surface normal at a point on a mesh
 * @param {THREE.Vector3} point - The point to get the normal at
 * @param {THREE.Mesh} mesh - The mesh to get the normal from
 * @param {Object} bvh - The bounding volume hierarchy of the mesh
 * @returns {THREE.Vector3} - The surface normal
 */
export const getSurfaceNormalAtPoint = (point, mesh, bvh) => {
  if (!mesh || !bvh) return new THREE.Vector3(0, 1, 0); // Default up vector

  try {
    // BVH operates in local space, so transform rays accordingly
    const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
    const localToWorld = mesh.matrixWorld;

    const origin = new THREE.Vector3(point.x, point.y, point.z);

    // Cast rays from multiple directions to find the closest surface point
    const directions = [
      new THREE.Vector3(0, 1, 0),   // Up
      new THREE.Vector3(0, -1, 0),  // Down
      new THREE.Vector3(1, 0, 0),   // Right
      new THREE.Vector3(-1, 0, 0),  // Left
      new THREE.Vector3(0, 0, 1),   // Forward
      new THREE.Vector3(0, 0, -1),  // Back
    ];

    let closestHit = null;
    let closestDistance = Infinity;

    for (const direction of directions) {
      // Offset the origin slightly in the opposite direction
      const offsetOrigin = origin.clone().add(direction.clone().multiplyScalar(-0.1));

      // Transform to local space for BVH raycast
      const localRay = new THREE.Ray();
      localRay.origin.copy(offsetOrigin).applyMatrix4(worldToLocal);
      localRay.direction.copy(direction).transformDirection(worldToLocal).normalize();

      const hit = bvh.raycastFirst(localRay);
      if (hit && hit.face) {
        const distance = hit.distance;
        if (distance < closestDistance) {
          closestDistance = distance;
          closestHit = hit;
        }
      }
    }
    
    if (closestHit && closestHit.face) {
      // Get the face normal in world space
      const face = closestHit.face;
      const geometry = mesh.geometry;
      const positions = geometry.attributes.position;

      // Get face vertices
      const a = new THREE.Vector3().fromBufferAttribute(positions, face.a);
      const b = new THREE.Vector3().fromBufferAttribute(positions, face.b);
      const c = new THREE.Vector3().fromBufferAttribute(positions, face.c);

      // Transform to world space
      a.applyMatrix4(mesh.matrixWorld);
      b.applyMatrix4(mesh.matrixWorld);
      c.applyMatrix4(mesh.matrixWorld);

      // Calculate face normal
      const normal = new THREE.Vector3();
      const cb = c.clone().sub(b);
      const ab = a.clone().sub(b);
      normal.crossVectors(cb, ab).normalize();

      // Ensure normal points outward (away from mesh center)
      const meshCenter = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
      // Transform hit point to world space for comparison
      const worldHitPoint = new THREE.Vector3().copy(closestHit.point).applyMatrix4(localToWorld);
      const toCenter = meshCenter.clone().sub(worldHitPoint);
      if (normal.dot(toCenter) > 0) {
        normal.negate();
      }

      return normal;
    }
    
    // Fallback: use the direction from mesh center to point as approximate normal
    const meshCenter = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
    const normal = new THREE.Vector3().subVectors(origin, meshCenter).normalize();
    return normal;
    
  } catch (error) {
    console.error('Error calculating surface normal:', error);
    return new THREE.Vector3(0, 1, 0); // Default up vector
  }
};

/**
 * Convert world positions float32array to THREE.Vector3 objects
 * @param {Float32Array} worldPositions - World positions
 * @returns {THREE.Vector3[]} - Points in world space
 */
export function convertToWorldPositions(worldPositions) {
    const pointsWorld = [];
    for (let i = 0; i < worldPositions.length; i += 3) {
      const x = worldPositions[i];
      const y = worldPositions[i + 1];
      const z = worldPositions[i + 2];
      if (isFinite(x) && isFinite(y) && isFinite(z)) {
        pointsWorld.push(new THREE.Vector3(x, y, z));
      }
    }
    return pointsWorld;
}

/**
 * Get the world bounds of a mesh
 * @param {THREE.Mesh} mesh - The mesh to get the bounds from
 * @returns {Object} - The center and size of the mesh
 */
export const getMeshWorldBounds = (mesh) => {
  const bbox = (mesh.geometry.boundingBox ? mesh.geometry.boundingBox.clone() : (mesh.geometry.computeBoundingBox(), mesh.geometry.boundingBox.clone()));
  bbox.applyMatrix4(mesh.matrixWorld);
  const center = bbox.getCenter(new THREE.Vector3());
  const size = bbox.getSize(new THREE.Vector3());
  return { center, size };
};